import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { setCampaignBillingAccess } from '@/lib/adminApi';
import type { User, Session } from '@supabase/supabase-js';

interface AdminAuthState {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  /** 'owner' | 'director' | staff roles; null until verified. */
  role: string | null;
  isOwner: boolean;
  /** Owner or director: full access and can manage staff. */
  isManager: boolean;
  /** Portal areas this person may open (every area for owner and directors). */
  areas: string[];
  /**
   * Owner and directors only: revenue, invoices, payments, deal rates and
   * booked sponsorship totals. The server strips these for staff too.
   */
  seesMoney: boolean;
  /** Whether the signed-in admin may open the given area; null means everyone. */
  canAccess: (area: string | null) => boolean;
  displayName: string | null;
  /**
   * Signed in with one-time details from the owner. No portal access until
   * they choose their own password (completePasswordSetup).
   */
  needsPasswordSetup: boolean;
  completePasswordSetup: (password: string) => Promise<void>;
  /**
   * Admin account whose session has no recent authenticator code. No portal
   * access until they set up or enter one (TwoStepSignIn).
   */
  needsTwoStep: boolean;
  /** Re-reads access after a two-step code is entered. */
  refreshAccess: () => Promise<void>;
  loading: boolean;
  error: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  /**
   * Completes a magic-link sign-in with the one-time code from the email.
   * The project's Supabase email templates send `{{ .Token }}` (an 8-digit
   * code, consumed by the mobile app's OTP screens) rather than a link, so
   * the portal must offer somewhere to type it.
   */
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [areas, setAreas] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [needsTwoStep, setNeedsTwoStep] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const verifyAdmin = useCallback(async (userId: string) => {
    const { data: access, error: accessError } = await supabase.rpc('get_my_admin_access');
    const a = access as {
      ok?: boolean;
      is_admin?: boolean;
      status?: string | null;
      role?: string | null;
      display_name?: string | null;
      needs_password?: boolean;
      needs_two_step?: boolean;
      areas?: string[] | null;
    } | null;
    if (!accessError && a?.ok) {
      setRole(a.role ?? null);
      setAreas(Array.isArray(a.areas) ? a.areas : []);
      setDisplayName(a.display_name ?? null);
      if (a.is_admin && a.needs_two_step) {
        setIsAdmin(false);
        setNeedsPasswordSetup(false);
        setNeedsTwoStep(true);
        setError(null);
      } else if (a.is_admin) {
        setIsAdmin(true);
        setNeedsPasswordSetup(false);
        setNeedsTwoStep(false);
        setError(null);
      } else if (a.needs_password) {
        setIsAdmin(false);
        setNeedsPasswordSetup(true);
        setNeedsTwoStep(false);
        setError(null);
      } else {
        setIsAdmin(false);
        setNeedsPasswordSetup(false);
        setNeedsTwoStep(false);
        setError(
          a.status === 'disabled'
            ? 'This admin account has been switched off.'
            : 'Your account is not authorized for the admin portal.',
        );
        await supabase.auth.signOut();
      }
      return;
    }

    try {
      // Use list_platform_admins RPC (SECURITY DEFINER — bypasses RLS)
      // Response shape: { ok: true, admins: [{ user_id, full_name, created_at, ... }] }
      const { data, error: queryError } = await supabase
        .rpc('list_platform_admins');

      if (queryError) {
        console.error('[AdminAuth] RPC error:', queryError);
        setIsAdmin(false);
        setError('Failed to verify admin status. Please try again.');
        return;
      }

      // The RPC returns { ok: boolean, admins: Array<{ user_id: string, ... }> }
      const response = data as { ok?: boolean; admins?: Array<{ user_id: string }> } | null;
      const admins = response?.admins ?? [];
      const isUserAdmin = admins.some(
        (admin) => admin.user_id === userId
      );

      if (!isUserAdmin) {
        setIsAdmin(false);
        setError('Your account is not authorized for the admin portal.');
        await supabase.auth.signOut();
      } else {
        setIsAdmin(true);
        setError(null);
      }
    } catch (err) {
      console.error('[AdminAuth] Verification error:', err);
      setIsAdmin(false);
      setError('Failed to verify admin status');
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        verifyAdmin(s.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        verifyAdmin(s.user.id);
      } else {
        setIsAdmin(false);
        setNeedsPasswordSetup(false);
        setNeedsTwoStep(false);
        setRole(null);
        setAreas([]);
      }
    });

    return () => subscription.unsubscribe();
  }, [verifyAdmin]);

  // The server stops accepting an authenticator code after 12 hours. Re-check
  // now and then so the code screen appears instead of failing requests. Only
  // a successful answer changes state, so a network blip never signs anyone out.
  useEffect(() => {
    if (!user) return;
    const recheck = async () => {
      const { data, error: accessError } = await supabase.rpc('get_my_admin_access');
      const a = data as { ok?: boolean; is_admin?: boolean; needs_two_step?: boolean } | null;
      if (accessError || !a?.ok || !a.is_admin) return;
      if (a.needs_two_step) {
        setIsAdmin(false);
        setNeedsTwoStep(true);
      }
    };
    const timer = window.setInterval(recheck, 5 * 60 * 1000);
    const onFocus = () => void recheck();
    window.addEventListener('focus', onFocus);
    window.addEventListener('admin-two-step-required', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('admin-two-step-required', onFocus);
    };
  }, [user]);

  const refreshAccess = useCallback(async () => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (s?.user) await verifyAdmin(s.user.id);
  }, [verifyAdmin]);

  const signInWithPassword = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError(authError.message);
      setLoading(false);
      throw authError;
    }
    setLoading(false);
  };

  const signInWithMagicLink = async (email: string) => {
    setError(null);
    const { error: authError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Never mint a new auth user from the admin login page. Admins are
        // provisioned in platform_admins; an unknown email must fail here.
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/admin`,
      }
    });
    if (authError) {
      setError(authError.message);
      throw authError;
    }
  };

  const verifyEmailCode = async (email: string, code: string) => {
    setError(null);
    const { error: authError } = await supabase.auth.verifyOtp({
      email,
      token: code.replace(/\s+/g, ''),
      type: 'email',
    });
    if (authError) {
      setError(authError.message);
      throw authError;
    }
    // onAuthStateChange picks up the new session and runs verifyAdmin.
  };

  const completePasswordSetup = async (password: string) => {
    const { error: pwError } = await supabase.auth.updateUser({ password });
    if (pwError) throw pwError;
    const { data, error: rpcError } = await supabase.rpc('complete_admin_password_change');
    if (rpcError) throw rpcError;
    if (!(data as { ok?: boolean } | null)?.ok) {
      const reason = (data as { error?: string } | null)?.error;
      throw new Error(
        reason === 'password_not_changed'
          ? 'Choose a password different from the temporary one.'
          : 'Could not finish setting up your account. Ask the owner for new sign-in details.',
      );
    }
    if (user) await verifyAdmin(user.id);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setNeedsPasswordSetup(false);
    setNeedsTwoStep(false);
    setRole(null);
    setAreas([]);
    setError(null);
  };

  const isManager = role === 'owner' || role === 'director' || (isAdmin && role === null);
  // Set during render so pages mounting in this same commit already query
  // with the right campaign columns.
  setCampaignBillingAccess(isManager);
  const canAccess = useCallback(
    (area: string | null) => area === null || isManager || areas.includes(area),
    [isManager, areas],
  );

  return (
    <AdminAuthContext.Provider value={{
      user, session, isAdmin, loading, error,
      role, isOwner: role === 'owner', isManager, seesMoney: isManager, areas, canAccess, displayName, needsPasswordSetup, completePasswordSetup, needsTwoStep, refreshAccess,
      signInWithPassword, signInWithMagicLink, verifyEmailCode, signOut
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
